import { presetToRange } from './utils'

export type FilterState = {
  q: string
  clientIds: string[]
  actorIds: string[]
  excludeSystemActors: boolean
  category: string | null
  timeKey: string | null
  timeFrom: string | null
  timeTo: string | null
  cursor: string | null
  prevCursors: (string | null)[]
}

export const initialFilterState: FilterState = {
  q: '',
  clientIds: [],
  actorIds: [],
  excludeSystemActors: false,
  category: null,
  timeKey: null,
  timeFrom: null,
  timeTo: null,
  cursor: null,
  prevCursors: [],
}

export type FilterAction =
  | { type: 'set_q'; q: string }
  | { type: 'set_clients'; ids: string[] }
  | { type: 'reset_clients' }
  | { type: 'set_actors'; ids: string[] }
  | { type: 'reset_actors' }
  | { type: 'set_exclude_system'; value: boolean }
  | { type: 'set_category'; key: string | null }
  | { type: 'set_time'; key: string | null }
  | { type: 'page_next'; nextCursor: string | null }
  | { type: 'page_prev' }
  | { type: 'toggle_client'; id: string }
  | { type: 'toggle_actor'; id: string }
  | { type: 'toggle_category'; key: string }

function resetPaging(s: FilterState): FilterState {
  return { ...s, cursor: null, prevCursors: [] }
}

export function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'set_q':
      return resetPaging({ ...state, q: action.q })
    case 'set_clients':
      return resetPaging({ ...state, clientIds: action.ids })
    case 'reset_clients':
      return resetPaging({ ...state, clientIds: [] })
    case 'toggle_client': {
      const has = state.clientIds.includes(action.id)
      return resetPaging({
        ...state,
        clientIds: has ? state.clientIds.filter((x) => x !== action.id) : [...state.clientIds, action.id],
      })
    }
    case 'set_actors':
      return resetPaging({ ...state, actorIds: action.ids })
    case 'reset_actors':
      return resetPaging({ ...state, actorIds: [], excludeSystemActors: false })
    case 'toggle_actor': {
      const has = state.actorIds.includes(action.id)
      return resetPaging({
        ...state,
        actorIds: has ? state.actorIds.filter((x) => x !== action.id) : [...state.actorIds, action.id],
      })
    }
    case 'set_exclude_system':
      return resetPaging({ ...state, excludeSystemActors: action.value })
    case 'set_category':
      return resetPaging({ ...state, category: action.key })
    case 'toggle_category':
      return resetPaging({
        ...state,
        category: state.category === action.key ? null : action.key,
      })
    case 'set_time': {
      if (!action.key) {
        return resetPaging({ ...state, timeKey: null, timeFrom: null, timeTo: null })
      }
      const range = presetToRange(action.key)
      if (!range) return state
      return resetPaging({ ...state, timeKey: action.key, timeFrom: range.from, timeTo: range.to })
    }
    case 'page_next':
      if (!action.nextCursor) return state
      return { ...state, prevCursors: [...state.prevCursors, state.cursor], cursor: action.nextCursor }
    case 'page_prev': {
      if (state.prevCursors.length === 0) return state
      const prev = state.prevCursors[state.prevCursors.length - 1]
      return { ...state, prevCursors: state.prevCursors.slice(0, -1), cursor: prev }
    }
    default:
      return state
  }
}
